import React, { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Loader2, ExternalLink, Copy, RefreshCw, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatApiErrorDetail } from "@/lib/apiError";
import { compressImageIfNeeded } from "@/lib/imageCompress";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const OG_PUBLIC_PATH = "/api/uploads/branding/og-image.jpg";
const FB_DEBUGGER = "https://developers.facebook.com/tools/debug/";

// Production URL the user actually shares — used for the Force Refresh
// helpers, since the social-network caches care about the canonical domain.
const PROD_OG_URL = `https://kiwamistudios.com${OG_PUBLIC_PATH}`;

const OGImagePanel = () => {
  const { authedAxios } = useAuth();
  const { lang } = useLanguage();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'compressing' | 'uploading' | null
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ax = authedAxios();
      const res = await ax.get("/admin/og-image");
      setInfo(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setLoading(false);
    }
  }, [authedAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(lang === "fr" ? "Le fichier doit être une image." : "File must be an image.");
      return;
    }
    setBusy("compressing");
    try {
      // Mild client-side compress so even huge originals upload quickly —
      // the backend re-encodes to 1200×630 q90 anyway.
      const payload = await compressImageIfNeeded(file, {
        maxEdge: 2400,
        quality: 0.92,
        skipBelowBytes: 1 * 1024 * 1024,
      });
      setBusy("uploading");
      const form = new FormData();
      form.append("file", payload);
      const ax = authedAxios();
      const res = await ax.post("/admin/og-image/upload", form, {
        timeout: 5 * 60 * 1000,
      });
      setInfo(res.data);
      toast.success(
        lang === "fr"
          ? "Vignette de partage mise à jour ✅"
          : "Share thumbnail updated ✅",
      );
    } catch (err) {
      const detail =
        err?.code === "ERR_NETWORK"
          ? lang === "fr"
            ? "Erreur réseau — image trop volumineuse ou connexion instable."
            : "Network error — file too large or unstable connection."
          : formatApiErrorDetail(err);
      toast.error(detail);
    } finally {
      if (fileRef.current) fileRef.current.value = "";
      setBusy(null);
    }
  };

  const copyProdUrl = async () => {
    try {
      await navigator.clipboard.writeText(PROD_OG_URL);
      toast.success(lang === "fr" ? "URL copiée" : "URL copied");
    } catch {
      toast.error(lang === "fr" ? "Impossible de copier" : "Could not copy");
    }
  };

  const previewSrc = info?.url ? `${BACKEND}${info.url}` : null;
  const formattedSize = info?.size_bytes
    ? `${(info.size_bytes / 1024).toFixed(0)} KB`
    : "—";
  const formattedDims = info?.width && info?.height ? `${info.width} × ${info.height}` : "—";

  return (
    <div data-testid="admin-og-panel" className="space-y-10">
      <div>
        <p className="overline kw-accent-line">
          {lang === "fr" ? "Vignette de partage" : "Share thumbnail"}
        </p>
        <h2 className="mt-3 font-serif-display text-3xl md:text-4xl">
          {lang === "fr"
            ? "L'image affichée quand on partage votre site"
            : "The image shown when your site is shared"}
        </h2>
        <p className="mt-4 max-w-2xl text-sm md:text-base text-[#A19E9C] leading-relaxed">
          {lang === "fr"
            ? "Format recommandé : 1200×630 px (ratio 1.91:1). Tout upload est automatiquement recadré et compressé pour s'afficher parfaitement sur WhatsApp, Facebook, LinkedIn, X et Instagram."
            : "Recommended format: 1200×630 px (1.91:1 ratio). Every upload is auto-cropped and compressed to render perfectly on WhatsApp, Facebook, LinkedIn, X and Instagram."}
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Live preview — adapts to the 1200x630 aspect of the stored file */}
        <div className="space-y-4">
          <p className="overline">{lang === "fr" ? "Aperçu actuel" : "Current preview"}</p>
          <div
            className="relative bg-[#050505] border border-[#F2EBE5]/10 overflow-hidden"
            style={{ aspectRatio: "1200 / 630" }}
          >
            {loading ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#C5A059]" strokeWidth={1.5} />
              </div>
            ) : previewSrc ? (
              <img
                src={previewSrc}
                alt="OG share preview"
                className="w-full h-full object-contain"
                data-testid="og-preview-img"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-[#A19E9C] gap-2">
                <ImageIcon className="w-10 h-10 opacity-40" strokeWidth={1} />
                <span className="text-xs uppercase tracking-[0.25em]">
                  {lang === "fr" ? "Aucune image" : "No image"}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-6 text-xs text-[#A19E9C]">
            <span data-testid="og-info-dims">{formattedDims}</span>
            <span className="text-[#F2EBE5]/20">•</span>
            <span data-testid="og-info-size">{formattedSize}</span>
            <button
              type="button"
              onClick={load}
              className="ml-auto inline-flex items-center gap-1.5 hover:text-[#C5A059] transition-colors"
              data-testid="og-refresh-btn"
            >
              <RefreshCw className="w-3 h-3" strokeWidth={1.5} />
              {lang === "fr" ? "Actualiser" : "Refresh"}
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-6">
          <div>
            <p className="overline">{lang === "fr" ? "Remplacer la vignette" : "Replace thumbnail"}</p>
            <input
              type="file"
              accept="image/*"
              ref={fileRef}
              onChange={handleUpload}
              className="hidden"
              data-testid="og-file-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy !== null}
              data-testid="og-upload-btn"
              className="mt-4 inline-flex items-center gap-2 px-5 py-3 border border-[#C5A059]/50 hover:border-[#C5A059] text-[#C5A059] text-[11px] uppercase tracking-[0.25em] font-medium hover:bg-[#C5A059]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy === "compressing" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                  {lang === "fr" ? "Compression…" : "Compressing…"}
                </>
              ) : busy === "uploading" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                  {lang === "fr" ? "Upload…" : "Uploading…"}
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" strokeWidth={1.5} />
                  {lang === "fr" ? "Choisir une image" : "Choose an image"}
                </>
              )}
            </button>
          </div>

          {/* Force-refresh helpers — social networks cache OG images for
              7–30 days. These shortcuts make the admin propagate the new
              thumbnail without waiting. */}
          <div className="border-t border-[#F2EBE5]/10 pt-6">
            <p className="overline">
              {lang === "fr"
                ? "Forcer le rafraîchissement des réseaux"
                : "Force social refresh"}
            </p>
            <p className="mt-3 text-sm text-[#A19E9C] leading-relaxed max-w-md">
              {lang === "fr"
                ? "WhatsApp, Facebook, LinkedIn et X mettent la vignette en cache 7 à 30 jours. Utilisez ces outils pour pousser la nouvelle immédiatement."
                : "WhatsApp, Facebook, LinkedIn and X cache the thumbnail for 7–30 days. Use these helpers to push the new one right away."}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={copyProdUrl}
                data-testid="og-copy-url-btn"
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#F2EBE5]/20 hover:border-[#C5A059] text-[#F2EBE5] hover:text-[#C5A059] text-[10px] uppercase tracking-[0.25em] transition-colors"
              >
                <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />
                {lang === "fr" ? "Copier l'URL prod" : "Copy prod URL"}
              </button>
              <a
                href={FB_DEBUGGER}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="og-fb-debugger-link"
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#F2EBE5]/20 hover:border-[#C5A059] text-[#F2EBE5] hover:text-[#C5A059] text-[10px] uppercase tracking-[0.25em] transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                Facebook Debugger
              </a>
            </div>
            <p className="mt-4 text-xs text-[#A19E9C]/70 leading-relaxed max-w-md">
              {lang === "fr"
                ? "Astuce — collez l'URL prod dans le Facebook Debugger, puis cliquez « Scrape Again ». LinkedIn et Instagram récupèrent ensuite la nouvelle vignette automatiquement."
                : "Tip — paste the prod URL into the Facebook Debugger, click \"Scrape Again\". LinkedIn and Instagram auto-pull the new thumbnail afterwards."}
            </p>
          </div>
        </div>
      </div>

      {/* WhatsApp-style preview card so the admin instantly sees how the
          link will render in a chat. */}
      <div className="border-t border-[#F2EBE5]/10 pt-10">
        <p className="overline kw-accent-line">
          {lang === "fr" ? "Aperçu WhatsApp" : "WhatsApp preview"}
        </p>
        <div className="mt-6 max-w-md border border-[#F2EBE5]/15 bg-[#0a0a0a] rounded-lg overflow-hidden">
          {previewSrc && (
            <img
              src={previewSrc}
              alt="WhatsApp preview"
              className="w-full block"
              style={{ aspectRatio: "1200 / 630", objectFit: "cover" }}
            />
          )}
          <div className="px-4 py-3">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#A19E9C]">kiwamistudios.com</p>
            <p className="mt-1 text-sm font-medium text-[#F2EBE5]">
              Kiwami Studios &amp; Co — Photographie premium · Maroc
            </p>
            <p className="mt-1 text-xs text-[#A19E9C] line-clamp-2">
              {lang === "fr"
                ? "Studio de photographie premium au Maroc — BTP, événementiel, sport, corporate, publicité et formation. Devis sous 24h."
                : "Premium photography studio in Morocco — BTP, events, sport, corporate, advertising and training. Quote within 24h."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OGImagePanel;
